import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth } from '@/providers/AuthProvider'
import { usePermission } from '@/lib/permissions'
import { defaultResourceId } from '@/lib/resourceFilter'

/**
 * The Resource filter's state for one tab (z8nrz7fgzw): the person whose tasks
 * it shows, or null for everyone. Opens on the viewer's own tasks unless they
 * are Super Admin or a supervisor.
 *
 * The session can resolve after the tab mounts, so the default is applied
 * again once it does — until the viewer picks a filter themselves.
 */
export function useResourceFilter(): [string | null, (id: string | null) => void] {
  const { user } = useAuth()
  const { isSuperAdmin, roles } = usePermission()
  const userId = user?.userId ?? null
  const roleKey = roles.join(',')
  const [resourceId, setResourceIdState] = useState<string | null>(() =>
    defaultResourceId({ isSuperAdmin, roles, userId }),
  )
  const picked = useRef(false)
  useEffect(() => {
    if (picked.current) return
    setResourceIdState(defaultResourceId({ isSuperAdmin, roles: roleKey ? roleKey.split(',') : [], userId }))
  }, [isSuperAdmin, roleKey, userId])
  const setResourceId = useCallback((id: string | null) => {
    picked.current = true
    setResourceIdState(id)
  }, [])
  return [resourceId, setResourceId]
}
